import { supabase } from './supabase';

export const TAX_FREE_SYSTEM_CHANGE_DATE = new Date('2026-11-01');

export function isNewTaxFreeEra(date: Date | string): boolean {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d >= TAX_FREE_SYSTEM_CHANGE_DATE;
}

export function hasTripsInNewEra(trips: Array<{ end_date: string | null | undefined }>): boolean {
  return trips.some(t => !!t.end_date && isNewTaxFreeEra(t.end_date));
}

export interface TaxFreePurchase {
  id: string;
  user_id: string;
  trip_id: string | null;
  store_name: string;
  amount: number;
  tax_rate: number;
  category: 'general' | 'food';
  purchase_date: string;
  notes: string | null;
  created_at: string;
}

export type TaxFreePurchaseInput = Omit<
  TaxFreePurchase,
  'id' | 'user_id' | 'created_at'
>;

export async function getTaxFreePurchases(tripId?: string): Promise<TaxFreePurchase[]> {
  let query = supabase
    .from('tax_free_purchases')
    .select('*')
    .order('purchase_date', { ascending: false });

  if (tripId) query = query.eq('trip_id', tripId);

  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as TaxFreePurchase[];
}

export async function addTaxFreePurchase(
  purchase: TaxFreePurchaseInput,
): Promise<TaxFreePurchase> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('tax_free_purchases')
    .insert({ ...purchase, user_id: user.id })
    .select()
    .single();

  if (error) throw error;
  return data as TaxFreePurchase;
}

export async function updateTaxFreePurchase(
  id: string,
  updates: Partial<TaxFreePurchase>,
): Promise<void> {
  const { error } = await supabase
    .from('tax_free_purchases')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function deleteTaxFreePurchase(id: string): Promise<void> {
  const { error } = await supabase
    .from('tax_free_purchases')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

export interface StoreDay {
  store: string;
  date: string;
  total: number;
  isEligible: boolean;
  remaining: number;
  purchases: TaxFreePurchase[];
}

export const MIN_TAX_FREE_AMOUNT = 5000;

export function groupByStoreDay(purchases: TaxFreePurchase[]): StoreDay[] {
  const map = new Map<string, TaxFreePurchase[]>();

  for (const p of purchases) {
    const key = `${p.store_name.toLowerCase().trim()}|${p.purchase_date}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(p);
  }

  return Array.from(map.values())
    .map(pList => {
      const total = pList.reduce((sum, p) => sum + Number(p.amount), 0);
      return {
        store: pList[0].store_name,
        date: pList[0].purchase_date,
        total,
        isEligible: total >= MIN_TAX_FREE_AMOUNT,
        remaining: Math.max(0, MIN_TAX_FREE_AMOUNT - total),
        purchases: pList,
      };
    })
    .sort((a, b) => b.date.localeCompare(a.date) || a.store.localeCompare(b.store));
}

export interface RefundSummary {
  totalEligible: number;
  totalIneligible: number;
  estimatedRefund: number;
  eligibleCount: number;
  ineligibleCount: number;
  byTaxRate: {
    rate10: { amount: number; refund: number };
    rate8: { amount: number; refund: number };
  };
}

export interface PurchaseDeadline {
  purchase: TaxFreePurchase;
  deadlineDate: Date;
  daysRemaining: number;
  isExpired: boolean;
  isUrgent: boolean;
  isSoon: boolean;
}

export const REFUND_DEADLINE_DAYS = 90;

export function calculateDeadlines(purchases: TaxFreePurchase[]): PurchaseDeadline[] {
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  return purchases
    .map(p => {
      const purchaseDate = new Date(p.purchase_date);
      const deadlineDate = new Date(purchaseDate);
      deadlineDate.setDate(deadlineDate.getDate() + REFUND_DEADLINE_DAYS);

      const diffMs = deadlineDate.getTime() - now.getTime();
      const daysRemaining = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

      return {
        purchase: p,
        deadlineDate,
        daysRemaining,
        isExpired: daysRemaining < 0,
        isUrgent: daysRemaining >= 0 && daysRemaining <= 7,
        isSoon: daysRemaining >= 0 && daysRemaining <= 14,
      };
    })
    .sort((a, b) => a.daysRemaining - b.daysRemaining);
}

export function getEarliestDeadline(purchases: TaxFreePurchase[]): PurchaseDeadline | null {
  const eligibleIds = new Set(
    groupByStoreDay(purchases)
      .filter(g => g.isEligible)
      .flatMap(g => g.purchases.map(p => p.id)),
  );
  const deadlines = calculateDeadlines(purchases).filter(
    d => !d.isExpired && eligibleIds.has(d.purchase.id),
  );
  return deadlines.length > 0 ? deadlines[0] : null;
}

export function calculateRefund(purchases: TaxFreePurchase[]): RefundSummary {
  const groups = groupByStoreDay(purchases);

  let totalEligible = 0;
  let totalIneligible = 0;
  let eligibleCount = 0;
  let ineligibleCount = 0;
  const rate10 = { amount: 0, refund: 0 };
  const rate8 = { amount: 0, refund: 0 };

  for (const g of groups) {
    if (g.isEligible) {
      eligibleCount++;
      totalEligible += g.total;
      for (const p of g.purchases) {
        const amt = Number(p.amount);
        if (Number(p.tax_rate) === 10) {
          rate10.amount += amt;
          rate10.refund += amt * 0.1;
        } else {
          rate8.amount += amt;
          rate8.refund += amt * 0.08;
        }
      }
    } else {
      ineligibleCount++;
      totalIneligible += g.total;
    }
  }

  return {
    totalEligible,
    totalIneligible,
    estimatedRefund: Math.round(rate10.refund + rate8.refund),
    eligibleCount,
    ineligibleCount,
    byTaxRate: {
      rate10: { amount: rate10.amount, refund: Math.round(rate10.refund) },
      rate8: { amount: rate8.amount, refund: Math.round(rate8.refund) },
    },
  };
}
